import React, { useState, useEffect } from 'react';

function Avatar({ avatar, name, size = 36 }) {
  if (avatar) return <img className="avatar" src={`https://sleepercdn.com/avatars/thumbs/${avatar}`} alt={name} style={{ width: size, height: size }} onError={e => e.target.style.display = 'none'} />;
  return <div className="avatar-fallback" style={{ width: size, height: size, fontSize: size * 0.3 }}>{name?.slice(0,2).toUpperCase() || '??'}</div>;
}

export default function HeadToHead({ api }) {
  const [history, setHistory] = useState(null);
  const [standings, setStandings] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [h, s] = await Promise.all([
          fetch(`${api}/api/history`).then(r => r.json()),
          fetch(`${api}/api/standings`).then(r => r.json()),
        ]);
        setHistory(h);
        setStandings(s);
        if (s.length > 0) setSelected(s[0].owner_id);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [api]);

  const avatarMap = {};
  standings.forEach(s => { avatarMap[s.owner_id] = s.avatar; });

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading H2H data...</span></div>;

  if (!history?.h2h || !selected) return (
    <div className="empty-state">
      <div style={{ fontSize: '2rem', marginBottom: 12 }}>⚔️</div>
      <p>H2H history loading — check back in ~30 seconds.</p>
    </div>
  );

  const h2hMap = history.h2h || {};
  const myH2H = h2hMap[selected] || {};
  const entries = Object.entries(myH2H).sort((a, b) => (b[1].wins + b[1].losses) - (a[1].wins + a[1].losses));

  const selectedTeam = standings.find(s => s.owner_id === selected);
  const totalW = entries.reduce((s, [, v]) => s + v.wins, 0);
  const totalL = entries.reduce((s, [, v]) => s + v.losses, 0);
  const overallPct = totalW + totalL > 0 ? ((totalW / (totalW + totalL)) * 100).toFixed(1) : '—';

  // Find nemesis (most losses to) and victim (most wins against)
  const nemesis = entries.reduce((best, [id, r]) => (!best || r.losses > best[1].losses) ? [id, r] : best, null);
  const victim = entries.reduce((best, [id, r]) => (!best || r.wins > best[1].wins) ? [id, r] : best, null);

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Head-to-Head</div>
        <div className="page-subtitle">All-time matchup records across all {history.seasons_count} seasons</div>
      </div>

      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <select className="h2h-select" value={selected} onChange={e => setSelected(e.target.value)}>
          {standings.map(s => <option key={s.owner_id} value={s.owner_id}>{s.display_name}</option>)}
        </select>
        {selectedTeam && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar avatar={selectedTeam.avatar} name={selectedTeam.display_name} />
            <div>
              <div style={{ fontWeight: 600 }}>{selectedTeam.display_name}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                All-time vs field: {totalW}-{totalL} ({overallPct}%)
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fun stats */}
      {entries.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
          {nemesis && nemesis[1].losses > 0 && (
            <div className="card" style={{ background: 'rgba(255,71,87,0.06)', borderColor: 'rgba(255,71,87,0.2)' }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6, fontFamily: 'var(--font-mono)' }}>😤 Nemesis</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar avatar={avatarMap[nemesis[0]]} name={nemesis[1].name} size={32} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{nemesis[1].name}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--red)' }}>
                    {nemesis[1].wins}-{nemesis[1].losses} ({((nemesis[1].wins / (nemesis[1].wins + nemesis[1].losses)) * 100).toFixed(0)}% win rate)
                  </div>
                </div>
              </div>
            </div>
          )}
          {victim && victim[1].wins > 0 && (
            <div className="card" style={{ background: 'rgba(0,230,118,0.06)', borderColor: 'rgba(0,230,118,0.2)' }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--green)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6, fontFamily: 'var(--font-mono)' }}>😎 Favorite Opponent</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar avatar={avatarMap[victim[0]]} name={victim[1].name} size={32} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{victim[1].name}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--green)' }}>
                    {victim[1].wins}-{victim[1].losses} ({((victim[1].wins / (victim[1].wins + victim[1].losses)) * 100).toFixed(0)}% win rate)
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!entries.length ? (
        <div className="empty-state">
          <p>No head-to-head data found for this manager yet.</p>
        </div>
      ) : (
        <div className="h2h-grid">
          {entries.map(([oppOwnerId, record]) => {
            const total = record.wins + record.losses;
            const winPct = total > 0 ? Math.round((record.wins / total) * 100) : 0;
            const isWinning = record.wins > record.losses;
            const isTied = record.wins === record.losses;
            return (
              <div className="h2h-card" key={oppOwnerId} style={{
                borderColor: isWinning ? 'rgba(0,230,118,0.25)' : isTied ? 'rgba(255,211,42,0.25)' : 'rgba(255,71,87,0.15)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <Avatar avatar={avatarMap[oppOwnerId]} name={record.name} size={30} />
                  <div className="h2h-opponent">{record.name}</div>
                </div>
                <div className="h2h-record-display">
                  <span className="h2h-w">{record.wins}</span>
                  <span className="h2h-sep">-</span>
                  <span className="h2h-l">{record.losses}</span>
                  <span style={{
                    marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: '0.75rem', fontWeight: 600,
                    color: isWinning ? 'var(--green)' : isTied ? 'var(--yellow)' : 'var(--red)',
                  }}>{winPct}%</span>
                </div>
                <div className="h2h-pf-pa">
                  PF: {record.pf?.toFixed(1)} · PA: {record.pa?.toFixed(1)}
                </div>
                <div style={{ marginTop: 8, height: 3, background: 'var(--bg4)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${winPct}%`,
                    background: isWinning ? 'var(--green)' : 'var(--red)',
                    borderRadius: 2, transition: 'width 0.6s ease',
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
